/* eslint-disable react/jsx-props-no-spreading */
import React, { useMemo, useEffect } from 'react';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import {
  FormDialog,
  FormDialogActions,
  FormDialogContent,
  FormDialogTitle,
  FormElement,
  FormInputsStack,
  FormCancelButton,
  Inputs,
} from '@marageti/z4-lib';
import { useForm, Controller } from 'react-hook-form';
import {
  getDaysInMonth, parseISO, isValid,
} from 'date-fns';
import { type SignificantDate } from '@marageti/z4-sdk/lib/people';
import { YEAR_UNKNOWN_SENTINEL } from '../traveler-details/traveler-personal-details.utils';

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

interface FormValues {
  title: string;
  dateMonth: string;
  dateDay: string;
  dateYear: string;
}

interface EditSignificantDateModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (entry: SignificantDate) => void;
  initialValues?: SignificantDate;
  isCreate: boolean;
}

const getDateDefaults = (date: string | undefined): Pick<FormValues, 'dateMonth' | 'dateDay' | 'dateYear'> => {
  if (!date?.trim()) return { dateMonth: '', dateDay: '', dateYear: '' };
  const parsed = parseISO(date);
  if (!isValid(parsed)) return { dateMonth: '', dateDay: '', dateYear: '' };
  return {
    dateMonth: String(parsed.getUTCMonth() + 1),
    dateDay: String(parsed.getUTCDate()),
    dateYear: parsed.getUTCFullYear() !== YEAR_UNKNOWN_SENTINEL ? String(parsed.getUTCFullYear()) : '',
  };
};

export const EditSignificantDateModal = ({
  open,
  onClose,
  onSave,
  initialValues,
  isCreate,
}: EditSignificantDateModalProps) => {
  const currentYear = new Date().getFullYear();

  const defaultValues: FormValues = {
    title: initialValues?.title ?? '',
    ...getDateDefaults(initialValues?.date),
  };

  const {
    control,
    handleSubmit,
    reset,
    getValues,
    trigger,
    watch,
    formState: {
      errors, isSubmitting, isDirty, isValid: formIsValid,
    },
  } = useForm<FormValues>({
    mode: 'onChange',
    defaultValues,
  });

  useEffect(() => {
    if (open) {
      reset({
        title: initialValues?.title ?? '',
        ...getDateDefaults(initialValues?.date),
      });
    }
  }, [open, initialValues]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = (data: FormValues) => {
    const year = data.dateYear ? parseInt(data.dateYear, 10) : YEAR_UNKNOWN_SENTINEL;
    const dateString = new Date(Date.UTC(
      year,
      parseInt(data.dateMonth, 10) - 1,
      parseInt(data.dateDay, 10),
    )).toISOString().replace(/\.\d{3}Z$/, 'Z');
    onSave({
      id: initialValues?.id ?? '',
      title: data.title,
      date: dateString,
    });
    reset();
    onClose();
  };

  const watchMonth = watch('dateMonth');
  const watchYear = watch('dateYear');

  const maxDay = useMemo(() => {
    if (!watchMonth) return 31;
    const month = parseInt(watchMonth, 10);
    const year = watchYear ? parseInt(watchYear, 10) : 2001;
    return getDaysInMonth(new Date(year, month - 1, 1));
  }, [watchMonth, watchYear]);

  const watchTitle = watch('title');
  const watchDay = watch('dateDay');
  const isSaveDisabled = !watchTitle || !watchMonth || !watchDay || !formIsValid || (!isCreate && !isDirty);

  return (
    <FormDialog open={open}>
      <FormDialogTitle handleClose={handleClose}>Significant Dates</FormDialogTitle>
      <FormElement name="edit-significant-date-form" onSubmit={handleSubmit(onSubmit)}>
        <FormDialogContent>
          <FormInputsStack>
            <Controller
              control={control}
              name="title"
              rules={{ required: 'Required' }}
              render={({ field }) => (
                <Inputs.Text
                  {...field}
                  id="significant-date-title"
                  label="Title*"
                  error={!!errors.title}
                  helperText={errors.title?.message}
                />
              )}
            />
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: -1 }}>
                Date*
              </Typography>
              <Box display="flex" gap={1}>
                <Box flex={5}>
                  <Controller
                    control={control}
                    name="dateMonth"
                    rules={{
                      validate: (v) => {
                        const { dateDay, dateYear } = getValues();
                        if (!v && (dateDay || dateYear)) return 'Required';
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        select
                        id="significant-date-month"
                        label="Month"
                        size="small"
                        fullWidth
                        error={!!errors.dateMonth}
                        helperText={errors.dateMonth?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          trigger('dateDay');
                        }}
                      >
                        {MONTHS.map((month, i) => (
                          <MenuItem key={month} value={String(i + 1)}>
                            {month}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />
                </Box>
                <Box flex={3}>
                  <Controller
                    control={control}
                    name="dateDay"
                    rules={{
                      validate: (v) => {
                        const { dateMonth, dateYear } = getValues();
                        if (!v && (dateMonth || dateYear)) return 'Required';
                        if (v) {
                          const n = parseInt(v, 10);
                          if (!Number.isInteger(n) || n < 1) return 'Invalid';
                          if (dateMonth) {
                            const year = dateYear ? parseInt(dateYear, 10) : 2001;
                            const daysInMonth = getDaysInMonth(new Date(year, parseInt(dateMonth, 10) - 1, 1));
                            if (n > daysInMonth) return `Max ${daysInMonth}`;
                          } else if (n > 31) {
                            return 'Invalid';
                          }
                        }
                        return true;
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="significant-date-day"
                        label="Day"
                        type="number"
                        size="small"
                        fullWidth
                        inputProps={{ min: 1, max: maxDay }}
                        error={!!errors.dateDay}
                        helperText={errors.dateDay?.message}
                        onChange={(e) => {
                          field.onChange(e);
                          trigger('dateMonth');
                        }}
                      />
                    )}
                  />
                </Box>
                <Box flex={4}>
                  <Controller
                    control={control}
                    name="dateYear"
                    rules={{
                      validate: (v) => {
                        if (!v) return true;
                        const n = parseInt(v, 10);
                        return (Number.isInteger(n) && n >= 1901 && n <= currentYear) || 'Invalid year';
                      },
                    }}
                    render={({ field }) => (
                      <TextField
                        {...field}
                        id="significant-date-year"
                        label="Year"
                        type="number"
                        size="small"
                        fullWidth
                        error={!!errors.dateYear}
                        helperText={errors.dateYear?.message ?? 'Optional'}
                        onChange={(e) => {
                          field.onChange(e);
                          trigger(['dateMonth', 'dateDay', 'dateYear']);
                        }}
                      />
                    )}
                  />
                </Box>
              </Box>
            </Box>
          </FormInputsStack>
        </FormDialogContent>
        <FormDialogActions>
          <FormCancelButton onClick={handleClose} />
          <LoadingButton
            variant="contained"
            type="submit"
            loading={isSubmitting}
            disabled={isSaveDisabled}
          >
            Save
          </LoadingButton>
        </FormDialogActions>
      </FormElement>
    </FormDialog>
  );
};

export default EditSignificantDateModal;
